"use client";

import { extendTVSlot } from "@/lib/utils";
import { formatPrice } from "@/utils/priceUtils";
import { useMarket } from "@app/_context";
import useTranslate from "@app/_hooks/useTranslate";
import type { ComponentProps, ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { Paragraph } from "../ui/paragraph";
import PricePrefix from "./PricePrefix";
import PriceSuffix from "./PriceSuffix";

const priceVariants = tv({
  slots: {
    root: "flex gap-x-1",
    prefix: "empty:hidden",
    suffix: "",
    price: "",
  },
  variants: {
    textTransform: {
      none: {
        prefix: "",
        suffix: "",
        price: "",
      },
      uppercase: {
        prefix: "uppercase all-small-caps text-muted-foreground tracking-wider",
        suffix: "uppercase all-small-caps text-muted-foreground tracking-wider",
        price: "",
      },
    },
  },
  defaultVariants: {
    textTransform: "none",
  },
});

type PriceVariants = VariantProps<typeof priceVariants>;

type PriceSlots = Partial<typeof priceVariants.slots>;

type PriceProps = Omit<ComponentProps<typeof Paragraph>, "className"> &
  PriceVariants & {
    prefix?: ReactNode;
    suffix?: ReactNode;
    price?: number;
    allowFree?: boolean;
    className?: string | PriceSlots;
  };

export default function Price({
  prefix,
  suffix,
  price,
  allowFree = false,
  className,
  textTransform,
  ...props
}: PriceProps) {
  const {
    root: rootSlot,
    prefix: prefixSlot,
    suffix: suffixSlot,
  } = priceVariants({ textTransform });

  const { market } = useMarket().state;
  const t = useTranslate();

  if (price === undefined) {
    return null;
  }

  if (price === 0 && allowFree) {
    return (
      <Paragraph
        className={rootSlot({ className: extendTVSlot("root", className) })}
        {...props}
      >
        {t("free")}
      </Paragraph>
    );
  }

  return (
    <Paragraph
      className={rootSlot({ className: extendTVSlot("root", className) })}
      {...props}
    >
      <PricePrefix
        className={prefixSlot({ className: extendTVSlot("prefix", className) })}
      >
        {prefix}
      </PricePrefix>
      {formatPrice(price, market.serviceLocale)}
      <PriceSuffix
        className={suffixSlot({ className: extendTVSlot("suffix", className) })}
      >
        {suffix}
      </PriceSuffix>
    </Paragraph>
  );
}

Price.displayName = "Price";
Price.PricePrefix = PricePrefix;
Price.PriceSuffix = PriceSuffix;
